import React, { useState } from 'react'
import { X } from 'lucide-react'
import { BuffSelector } from '../BuffSelector'

/**
 * AGC: Tournament NFT Selection Modal
 * AGC: Reuses BuffSelector component for selecting NFTs before each tournament match
 * AGC: Each round requires new NFT selection - players can choose different NFTs per match
 */
interface TournamentNFTSelectionProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (selectedBuffs: number[]) => void
  playerAddress: string
  opponentAddress: string
  round: number
  matchNumber: number
  onNavigateToMarketplace?: () => void
}

export const TournamentNFTSelection: React.FC<TournamentNFTSelectionProps> = ({
  isOpen,
  onClose,
  onConfirm,
  playerAddress,
  opponentAddress,
  round,
  matchNumber,
  onNavigateToMarketplace
}) => {
  const [selectedBuffs, setSelectedBuffs] = useState<number[]>([])
  const [selectedPotions, setSelectedPotions] = useState<number[]>([])

  if (!isOpen) return null

  // AGC: Handle combined buff/potion array from BuffSelector
  const handleBuffSelection = (combinedItems: number[]) => {
    // AGC: Separate buffs (0-3) from potions (10000+) in combined array
    const buffs: number[] = []
    const potions: number[] = []
    
    for (const item of combinedItems) {
      if (item >= 10000) {
        // AGC: Potion (offset by 10000)
        potions.push(item - 10000)
      } else {
        // AGC: Buff (0-3)
        buffs.push(item)
      }
    }
    
    setSelectedBuffs(buffs)
    setSelectedPotions(potions)
  }

  const handleConfirm = () => {
    // AGC: Pass combined array (buffs + potions with offset) to parent
    // AGC: Parent components expect combined format for consistency
    const combined = [
      ...selectedBuffs,
      ...selectedPotions.map(id => 10000 + id) // AGC: Offset tokenIds represent potions
    ]
    onConfirm(combined)
    setSelectedBuffs([]) // AGC: Reset for next match
    setSelectedPotions([])
  }

  const handleClose = () => {
    setSelectedBuffs([]) // AGC: Reset on close
    setSelectedPotions([])
    onClose()
  }

  // AGC: Short address helper
  const shortAddress = (addr: string) => {
    if (!addr || addr.length < 10) return addr || 'Unknown'
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-slate-900 border border-purple-500/40 rounded-2xl shadow-2xl p-6 space-y-4">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-3 right-3 p-2 rounded-full bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
          aria-label="Close NFT selection"
        >
          <X className="w-4 h-4" />
        </button>

        {/* AGC: Header */}
        <div className="pb-3 border-b border-white/10">
          <h2 className="text-xl font-bold text-white mb-1">Select Battle Items</h2>
          <p className="text-sm text-slate-400">
            Round {round} - Match {matchNumber}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            vs {shortAddress(opponentAddress)}
          </p>
        </div>

        {/* AGC: Info message */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
          <p className="text-xs text-blue-300">
            💡 <strong>Note:</strong> NFTs are <strong>optional</strong> - you can play with 0, 1, or 2 buffs and up to 1 potion. 
            Selected NFTs will be escrowed and burned/refunded after the battle.
          </p>
        </div>

        {/* AGC: Buff Selector (reused component - handles both buffs and potions) */}
        <div className="max-h-[60vh] overflow-y-auto">
          <BuffSelector
            playerAddress={playerAddress}
            onBuffsSelected={handleBuffSelection}
            maxBuffs={2}
            disabled={false}
            onNavigateToMarketplace={onNavigateToMarketplace}
            variant="compact"
            allowBothBuffsAndPotions={true}  // AGC: Tournament supports both buffs and potions (like PvE/PvP V2)
          />
        </div>

        {/* AGC: Action buttons */}
        <div className="flex gap-3 pt-3 border-t border-white/10">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2.5 px-4 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-2.5 px-4 rounded-lg transition-all active:scale-95 shadow-lg shadow-purple-500/30"
          >
            {selectedBuffs.length === 0 && selectedPotions.length === 0
              ? 'Play Without Items'
              : `Confirm (${selectedBuffs.length} buff${selectedBuffs.length !== 1 ? 's' : ''}${selectedPotions.length > 0 ? ` + ${selectedPotions.length} potion${selectedPotions.length !== 1 ? 's' : ''}` : ''})`
            }
          </button>
        </div>
      </div>
    </div>
  )
}

